import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';

import Editor, { LoadingFallback, pluginPresets } from 'my-editor';

import Tooltip from '@mui/material/Tooltip';

const rootCss = ({ palette }) => ({
  '&.root_solution': {
    cursor: 'pointer',
    borderRadius: 8,

    '&:hover': {
      backgroundColor: palette.primary.light,
    },
  },
  '&.root_solutionHidden': {
    '& > *': { filter: 'blur(4px)' },
  },
});

export default function SolutionStepsEditor({
  blockSolutionClassName,
  className,
  decoratorProps,
  editorClassName,
  onToggleSolutionKey,
  showSolutions,
  value,
}) {
  const valuesArray = useMemo(
    () =>
      value?.blocks
        .reduce(
          (mem, block) => {
            const prevGroup = mem[mem.length - 1];
            if (!prevGroup.length || !!prevGroup[prevGroup.length - 1].data?.solution === !!block.data?.solution) {
              prevGroup.push(block);
            } else {
              mem.push([block]);
            }
            return mem;
          },
          [[]],
        )
        .map(blocks => ({ key: blocks[0].key, solution: !!blocks[0].data?.solution, value: { ...value, blocks } })),
    [value],
  );

  const [solutionKeys, setSolutionKeys] = useState({});
  const toggleSolutionKey = useCallback(
    sk =>
      setSolutionKeys(sks => {
        const newSolKeys = { ...sks };
        newSolKeys[sk] = !newSolKeys[sk];
        return newSolKeys;
      }),
    [],
  );
  useEffect(() => {
    if (!showSolutions) setSolutionKeys({});
  }, [showSolutions]);

  return valuesArray.map(({ key, solution, value: v }) => {
    const editor = (
      <Editor
        canToggleSolutions
        className={editorClassName}
        decoratorProps={decoratorProps}
        fallback={<LoadingFallback value={v} />}
        onToggleSolutionKey={onToggleSolutionKey || toggleSolutionKey}
        plugins={pluginPresets.ALL}
        readOnly
        showSolutions={solution || showSolutions || solutionKeys}
        value={v}
      />
    );

    // When the showSolutions prop is true, all solutions are visibile and
    // cannot be toggled. In this state, we don't want to show the UI for
    // toggling the solution.
    const canToggleSolutionState = showSolutions !== true;

    if (solution && canToggleSolutionState) {
      const solutionHidden = solution && !showSolutions?.[key] && !solutionKeys[key];

      return (
        <Tooltip key={key} title={solutionHidden ? 'Show Solution' : 'Hide Solution'}>
          <div
            className={classNames(className, 'root_solution', blockSolutionClassName, {
              root_solutionHidden: solutionHidden,
            })}
            css={rootCss}
            onClick={() => toggleSolutionKey(key)}
          >
            {editor}
          </div>
        </Tooltip>
      );
    }

    return (
      <div key={key} className={className} css={rootCss}>
        {editor}
      </div>
    );
  });
}
